import { useState, useEffect } from "react";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import { ISkillPath } from "../../model/SkillPath.model";
import { ISkillGroup } from "../../model/SkillGroup.model";
import { ISkill } from "../../model/Skill.model";
import "./AddCompetency.scss";

//exclude discipline specific skills from available competency skills
const EXCLUDED_SKILL_PATH_IDS = [2];

interface AddCompetencyProps {
  existingSkillIds: string[];
  onAdd(skillId: string): void;
  onCancel(): void;
}

const AddCompetency: React.FC<AddCompetencyProps> = ({ existingSkillIds, onAdd, onCancel }) => {
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiCFSkillPath.scopes.read,
  });

  const [skillPaths, setSkillPaths] = useState<ISkillPath[]>([]);
  const [selectedSkillPath, setSelectedSkillPath] = useState<ISkillPath | null>(null);
  const [loadingSkillPath, setLoadingSkillPath] = useState(false);
  const [selectedSkillGroupId, setSelectedSkillGroupId] = useState<string>("");
  const [selectedSkillId, setSelectedSkillId] = useState<string>("");

  useEffect(() => {
    execute("GET", "/api/SkillPathsTop", undefined)
      .then((response: ISkillPath[]) =>
        setSkillPaths((response ?? []).filter((sp) => !EXCLUDED_SKILL_PATH_IDS.includes(Number(sp.skillPathId))))
      );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSkillPathChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const skillPathId = e.target.value;
    setSelectedSkillGroupId("");
    setSelectedSkillId("");
    setSelectedSkillPath(null);
    if (!skillPathId) return;
    setLoadingSkillPath(true);
    execute("GET", `/api/SkillPath/${skillPathId}`, undefined)
      .then((response: ISkillPath) => setSelectedSkillPath(response))
      .finally(() => setLoadingSkillPath(false));
  };

  const handleSkillGroupChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSkillGroupId(e.target.value);
    setSelectedSkillId("");
  };

  const skillGroups: ISkillGroup[] = selectedSkillPath?.skillGroups ?? [];
  const selectedSkillGroup = skillGroups.find((sg) => `${sg.skillGroupId}` === selectedSkillGroupId) ?? null;
  const skills: ISkill[] = selectedSkillGroup?.skills ?? [];

  const resetFields = () => {
    setSelectedSkillPath(null);
    setSelectedSkillGroupId("");
    setSelectedSkillId("");
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedSkillId) return;
    onAdd(selectedSkillId);
    resetFields();
  };

  const handleCancel = () => {
    resetFields();
    onCancel();
  };

  const formatSkillName = (nm: string) =>{
    if(!nm.endsWith("Skills")){
      return nm + " Skills";
    }
    return nm;
  }

  return (
    <form onSubmit={handleSubmit} className="modal-form add-competency-form">
      <div className="modal-form-content-scroll-container">
        <div className="form-group m-b-1">
          <label htmlFor="addCompSkillPath">Framework</label>
          <select
            id="addCompSkillPath"
            value={selectedSkillPath?.skillPathId ?? ""}
            onChange={handleSkillPathChange}
            required
          >
            <option value="">Select a skill framework</option>
            {skillPaths.map((sp) => (
              <option key={sp.skillPathId} value={sp.skillPathId}> {formatSkillName(sp.skillPathName)}</option>
            ))}
          </select>
        </div>

        <div className="form-group m-b-1">
          <label htmlFor="addCompSkillGroup">Skill Category</label>
          <select
            id="addCompSkillGroup"
            value={selectedSkillGroupId}
            onChange={handleSkillGroupChange}
            disabled={!selectedSkillPath || loadingSkillPath}
            required
          >
            <option value="">{loadingSkillPath ? "Loading..." : "Select a skill category"}</option>
            {skillGroups.map((sg) => (
              <option key={sg.skillGroupId} value={sg.skillGroupId}>{sg.skillGroupName}</option>
            ))}
          </select>
        </div>

        <div className="form-group m-b-1">
          <label htmlFor="addCompSkill">Competency/Skill</label>
          <select
            id="addCompSkill"
            value={selectedSkillId}
            onChange={(e) => setSelectedSkillId(e.target.value)}
            disabled={!selectedSkillGroup}
            required
          >
            <option value="">Select a competency/skill</option>
            {skills.map((s) => {
              const alreadyAdded = existingSkillIds.includes(`${s.SkillId}`);
              return (
                <option key={s.SkillId} value={s.SkillId} disabled={alreadyAdded}>
                  {s.skillName}{alreadyAdded ? " (already added)" : ""}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <div className="modal-form-button-container two-button">
        <button type="button" className="btn-med-gray" onClick={handleCancel}>Cancel</button>
        <button type="submit" className="btn-secondary" disabled={!selectedSkillId}>Add</button>
      </div>
    </form>
  );
};

export default AddCompetency;
